// Application entry point with routing
import m from 'mithril';

// Pages
import { Account } from './pages/Account';
import { Download } from './pages/Download';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { NotFound } from './pages/NotFound';
import { RequestInvite } from './pages/RequestInvite';
import { Signup } from './pages/Signup';
import { Terms } from './pages/Terms';
import { VerifyEmail } from './pages/VerifyEmail';

// utilities
import { AppShell } from './components/AppShell';
import { startAuthProfileStore } from './services/authStore';
import { initSentry } from './services/sentry';
import { setTitleForPath } from './routes';

// CSS
import './index.css';

// Webawesome
import { registerIconLibrary } from '@awesome.me/webawesome';
import '@awesome.me/webawesome/dist/styles/themes/default.css';
import '@awesome.me/webawesome/dist/styles/webawesome.css';

// Sentry
initSentry();

// Configure Webawesome icons to use Material Symbols when requested
registerIconLibrary('material', {
  resolver: (name) => {
    const match = name.match(/^(.*?)(_(rounded|sharp))?$/);
    if (match) {
      return `https://cdn.jsdelivr.net/npm/@material-symbols/svg-400@0.32.0/${match[3] ?? 'outlined'}/${match[1]}.svg`;
    }
    return '';
  },
  mutator: (svg) => svg.setAttribute('fill', 'currentColor'),
});

// Lit (used by Web Awesome components) generates spurious update warnings in development mode only
// this does not affect production builds
if (import.meta.env.DEV) {
  console.log('silencing lit update warnings');
  const { LitElement } = await import('lit');
  LitElement.disableWarning?.('change-in-update');
}


const root = document.querySelector<HTMLElement>('#app');

if (!root) {
  throw new Error('Missing #app root element.');
}

const withShell = <T extends m.Attributes>(component: m.ComponentTypes<T>): m.RouteResolver<T> => {
  return {
    render: (vnode) => {
      setTitleForPath(m.route.get());
      return m(AppShell, {
        content: m(component, vnode.attrs),
      });
    },
  };
};

const startApp = (): void => {

  // critical feature checks done early
  if (typeof HTMLElement.prototype.attachInternals !== 'function') {
    root.textContent = 'Your browser is unsupported. This site requires Chrome 77+, Firefox 93+, or Safari 16.4+.';
    return;
  }

  startAuthProfileStore();
  m.route.prefix = '';

  // Ask Mithril to render the page, our component gets placed into the root element.
  // Mithril will rerender automatically after DOM event handlers defined in component
  // views and also whenever m.redraw() is called.
  m.route(root, '/', {
    '/': withShell(Home),
    '/login': withShell(Login),
    '/login/:redirect': withShell(Login),
    '/account': withShell(Account),
    '/download': withShell(Download),
    '/download/:newUser': withShell(Download),
    '/signup': withShell(Signup),
    '/signup/:ref': withShell(Signup),
    '/request-invite': withShell(RequestInvite),
    '/verifyemail': withShell(VerifyEmail),
    '/terms': withShell(Terms),
    '/:404...': withShell(NotFound),
  });
};

// render the app
startApp();
